
import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import ApiClient from "../utils/api";
import {
  Search, MoreVertical, Trash2,
  ChevronLeft, ChevronRight, AlertCircle, Eye, ChevronDown
} from "lucide-react";
import DeleteConfirmModal from "../components/DeleteConfirmModal";


export default function Contacts({ activeAiAgentId }) {
  const navigate = useNavigate();
  const [contacts, setContacts] = useState([]);
  const [totalContacts, setTotalContacts] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Pagination & Search States
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  // Selection state
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [activeMenuId, setActiveMenuId] = useState(null);



  const [deleteModalConfig, setDeleteModalConfig] = useState({
    isOpen: false,
    title: "Delete Contact",
    message: "Are you sure you want to delete this contact? This action is permanent and cannot be undone.",
    onConfirm: () => {}
  });

  const channelOptions = [
    { value: "", label: "All Channels" },
    { value: "email", label: "Email" },
    { value: "freshchat", label: "Freshchat" },
    { value: "freshdesk", label: "Freshdesk" },
    { value: "slack", label: "Slack" },
    { value: "telegram", label: "Telegram" },
    { value: "webchat", label: "Webchat" },
    { value: "webhook", label: "Webhook" },
    { value: "whatsapp", label: "WhatsApp" },
    { value: "widget", label: "Widget Chat" },
  ];

  const [isChannelDropdownOpen, setIsChannelDropdownOpen] = useState(false);
  const channelDropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (channelDropdownRef.current && !channelDropdownRef.current.contains(event.target)) {
        setIsChannelDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const aiAgentId = activeAiAgentId || ApiClient.currentAiAgentId;

  // Reset page and selection when agent changes
  useEffect(() => {
    setCurrentPage(1);
    setSelectedIds(new Set());
    setActiveMenuId(null);
  }, [aiAgentId]);

  const [selectedChannels, setSelectedChannels] = useState([]);

  const fetchContacts = useCallback(async (page = currentPage, search = debouncedSearch, channels = selectedChannels.join(",")) => {
    setLoading(true);
    setError(null);
    try {
      const response = await ApiClient.getContacts(page, itemsPerPage, search, channels);
      setContacts(response?.contacts || []);
      setTotalContacts(response?.total_count || 0);
    } catch (err) {
      console.error("Error fetching contacts:", err);
      setError("Failed to load contacts");
    } finally {
      setLoading(false);
    }
  }, [currentPage, debouncedSearch, selectedChannels, itemsPerPage]);

  // Fetch contacts when page, search queries, or channels change
  useEffect(() => {
    fetchContacts(currentPage, debouncedSearch, selectedChannels.join(","));
 
  }, [aiAgentId, currentPage, debouncedSearch, selectedChannels, fetchContacts]);

  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
      setCurrentPage(1);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  const handleRowClick = (e, contactId) => {
    // Don't navigate if clicking checkbox, actions menu, links
    if (
      e.target.closest('.col-checkbox') ||
      e.target.closest('.col-actions') ||
      e.target.closest('.custom-checkbox') ||
      e.target.closest('.email-link')
    ) {
      return;
    }
    navigate(`/contacts/${contactId}`);
  };

  // Selection handlers
  const handleSelectAll = (e) => {
    if (e.target.checked) {
      const allIds = contacts.map(c => c.id);
      setSelectedIds(new Set(allIds));
    } else {
      setSelectedIds(new Set());
    }
  };

  const handleSelectRow = (id) => {
    const nextSelected = new Set(selectedIds);
    if (nextSelected.has(id)) {
      nextSelected.delete(id);
    } else {
      nextSelected.add(id);
    }
    setSelectedIds(nextSelected);
  };



  const handleDeleteClick = (contactId) => {
    setDeleteModalConfig({
      isOpen: true,
      title: "Delete Contact",
      message: "Are you sure you want to delete this contact? This action is permanent and cannot be undone.",
      onConfirm: async () => {
        try {
          await ApiClient.deleteContact(contactId);
          setDeleteModalConfig(prev => ({ ...prev, isOpen: false }));
          if (contacts.length === 1 && currentPage > 1) {
            setCurrentPage(p => p - 1);
          } else {
            fetchContacts(currentPage, debouncedSearch);
          }
        } catch (err) {
          console.error("Error deleting contact:", err);
        }
      }
    });
    setActiveMenuId(null);
  };

  const handleBulkDelete = () => {
    if (selectedIds.size === 0) return;
    setDeleteModalConfig({
      isOpen: true,
      title: "Delete Selected Contacts",
      message: `Are you sure you want to delete the ${selectedIds.size} selected contacts? This action is permanent and cannot be undone.`,
      onConfirm: async () => {
        setLoading(true);
        try {
          await Promise.all(Array.from(selectedIds).map(id => ApiClient.deleteContact(id)));
          setSelectedIds(new Set());
          setDeleteModalConfig(prev => ({ ...prev, isOpen: false }));
          fetchContacts(currentPage, debouncedSearch);
        } catch (err) {
          console.error("Error bulk deleting contacts:", err);
        } finally {
          setLoading(false);
        }
      }
    });
  };

  const startItem = totalContacts === 0 ? 0 : (currentPage - 1) * itemsPerPage + 1;
  const endItem = Math.min(currentPage * itemsPerPage, totalContacts);

  return (
    <>
      <style>{`

.easy-body-wrapper {
  flex: 1;
  padding: 20px;
  overflow: hidden;
  background-color: #f4f6fa;
  display: flex;
  height: calc(100vh - 120px);
}

/* Connected layout card holding both panels seamlessly */
.connected-panels-card {
  display: flex;
  flex: 1;
  background-color: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05);
  overflow: hidden;
  width: 100%;
  height: 100%;
  position: relative;
}

.body-left-part {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-width: 0;
  overflow: hidden;
}

.body-right-part {
  display: flex;
  flex-direction: column;
  overflow: hidden;
  flex-shrink: 0;
}

/* Resize Handler Divider Line */
.resize-divider {
  width: 10px;
  cursor: col-resize;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  background-color: transparent;
  transition: background-color 0.15s ease;
  flex-shrink: 0;
  z-index: 10;
}

.resize-divider:hover,
.resize-divider.dragging {
  background-color: #e0f2fe;
}

.resize-handle-line {
  position: absolute;
  top: 0;
  bottom: 0;
  left: 50%;
  width: 1px;
  background-color: #e2e8f0;
  transform: translateX(-50%);
  transition: background-color 0.15s ease, width 0.15s ease;
}

.resize-divider:hover .resize-handle-line,
.resize-divider.dragging .resize-handle-line {
  background-color: #0284c7;
  width: 2px;
}

.resize-handle-dots {
  display: flex;
  flex-direction: column;
  gap: 3px;
  background-color: #ffffff;
  padding: 6px 3px;
  border-radius: 4px;
  border: 1px solid #e2e8f0;
  box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05);
  transition: border-color 0.15s ease;
  pointer-events: none;
  z-index: 2;
}

.resize-divider:hover .resize-handle-dots,
.resize-divider.dragging .resize-handle-dots {
  border-color: #0284c7;
}

.resize-handle-dots span {
  width: 4px;
  height: 4px;
  border-radius: 50%;
  background-color: #94a3b8;
  transition: background-color 0.15s ease;
}

.resize-divider:hover .resize-handle-dots span,
.resize-divider.dragging .resize-handle-dots span {
  background-color: #0284c7;
}

/* Contacts Table Component (Connected variant) */
.contacts-panel-wrapper {
  display: flex;
  flex-direction: column;
  height: 100%;
  background-color: transparent;
  border: none;
  border-radius: 0;
  box-shadow: none;
}

.table-toolbar {
  padding: 14px 18px;
  border-bottom: 1px solid #e2e8f0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  background-color: #ffffff;
  flex-shrink: 0;
  gap: 16px;
  flex-wrap: wrap;
}

.toolbar-left {
  display: flex;
  align-items: center;
  gap: 16px;
}

.select-all-label {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  font-weight: 555;
  color: #475569;
  user-select: none;
  cursor: pointer;
}

.custom-checkbox {
  width: 16px;
  height: 16px;
  border: 1.5px solid #94a3b8;
  border-radius: 4px;
  accent-color: #0284c7;
  cursor: pointer;
}

.search-input-wrapper {
  position: relative;
  display: flex;
  align-items: center;
}

.search-icon {
  position: absolute;
  left: 10px;
  color: #94a3b8;
}

.search-field {
  padding: 8px 12px 8px 32px;
  font-size: 13px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  width: 210px;
  color: #0f172a;
  background-color: #f4f6fa;
}

.search-field:focus {
  outline: none;
  border-color: #0284c7;
  background-color: #ffffff;
  box-shadow: 0 0 0 3px rgba(2, 132, 199, 0.1);
}

.toolbar-right {
  display: flex;
  align-items: center;
  gap: 8px;
}

.toolbar-btn {
  background-color: #ffffff;
  border: 1px solid #e2e8f0;
  padding: 8px 12px;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
  color: #475569;
  display: flex;
  align-items: center;
  gap: 6px;
  transition: all 0.15s ease;
}

.toolbar-btn:hover {
  border-color: #cbd5e1;
  color: #0f172a;
  background-color: #f8fafc;
}

.btn-syncing {
  background-color: #f0f9ff;
  border-color: #bae6fd;
  color: #0284c7;
}

.pagination-wrapper {
  display: flex;
  align-items: center;
  gap: 12px;
  border-left: 1px solid #e2e8f0;
  padding-left: 12px;
  margin-left: 4px;
}

.pagination-text {
  font-size: 12px;
  color: #475569;
  font-weight: 555;
}

.pagination-actions {
  display: flex;
  gap: 4px;
}

.pagination-arrow-btn {
  background: none;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #475569;
}

.pagination-arrow-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.pagination-arrow-btn:not(:disabled):hover {
  border-color: #cbd5e1;
  color: #0f172a;
}

.table-overflow-container {
  flex: 1;
  overflow: auto;
}

.contacts-data-table {
  width: 100%;
  border-collapse: collapse;
  text-align: left;
}

.contacts-data-table th {
  padding: 10px 16px;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  color: #475569;
  background-color: #f8fafc;
  border-bottom: 1.5px solid #e2e8f0;
  position: sticky;
  top: 0;
  z-index: 2;
}

.contacts-data-table td {
  padding: 12px 16px;
  font-size: 13px;
  color: #0f172a;
  border-bottom: 1px solid #e2e8f0;
  vertical-align: middle;
}

.contact-row {
  transition: background-color 0.15s ease;
}

.contact-row:hover {
  background-color: #f8fafc;
}

.contact-row.row-selected {
  background-color: #f0f9ff;
}

.col-checkbox {
  width: 40px;
  text-align: center;
}

.col-contact {
  min-width: 160px;
}

.col-username {
  width: 160px;
  max-width: 160px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.col-session {
  width: 290px;
  white-space: nowrap;
}

.col-channel {
  width: 110px;
  white-space: nowrap;
}

.contact-cell {
  display: flex;
  align-items: center;
  gap: 12px;
}

.avatar-wrapper {
  position: relative;
  width: 32px;
  height: 32px;
  flex-shrink: 0;
}

.contact-avatar-img {
  width: 100%;
  height: 100%;
  border-radius: 50%;
  object-fit: cover;
}

.contact-avatar-letter {
  width: 100%;
  height: 100%;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 13px;
  color: white;
  text-shadow: 0 1px 1px rgba(0,0,0,0.1);
}

.avatar-theme-a { background: linear-gradient(135deg, #f59e0b, #d97706); }
.avatar-theme-b { background: linear-gradient(135deg, #3b82f6, #1d4ed8); }
.avatar-theme-c { background: linear-gradient(135deg, #10b981, #047857); }
.avatar-theme-d { background: linear-gradient(135deg, #8b5cf6, #6d28d9); }
.avatar-theme-e { background: linear-gradient(135deg, #ec4899, #be185d); }
.avatar-theme-f { background: linear-gradient(135deg, #6366f1, #4f46e5); }
.avatar-theme-g { background: linear-gradient(135deg, #06b6d4, #0891b2); }
.avatar-theme-h { background: linear-gradient(135deg, #14b8a6, #0f766e); }
.avatar-theme-i { background: linear-gradient(135deg, #f43f5e, #be123c); }
.avatar-theme-j { background: linear-gradient(135deg, #e11d48, #9f1239); }
.avatar-theme-k { background: linear-gradient(135deg, #4f46e5, #3730a3); }
.avatar-theme-l { background: linear-gradient(135deg, #0891b2, #075985); }
.avatar-theme-m { background: linear-gradient(135deg, #d97706, #92400e); }
.avatar-theme-n { background: linear-gradient(135deg, #059669, #065f46); }
.avatar-theme-o { background: linear-gradient(135deg, #2563eb, #1e3a8a); }
.avatar-theme-p { background: linear-gradient(135deg, #9333ea, #581c87); }
.avatar-theme-q { background: linear-gradient(135deg, #db2777, #831843); }
.avatar-theme-r { background: linear-gradient(135deg, #ea580c, #7c2d12); }
.avatar-theme-s { background: linear-gradient(135deg, #7c3aed, #4c1d95); }
.avatar-theme-t { background: linear-gradient(135deg, #0284c7, #0c4a6e); }
.avatar-theme-u { background: linear-gradient(135deg, #0d9488, #115e59); }
.avatar-theme-v { background: linear-gradient(135deg, #2563eb, #1d4ed8); }
.avatar-theme-w { background: linear-gradient(135deg, #4f46e5, #4338ca); }
.avatar-theme-x { background: linear-gradient(135deg, #7c3aed, #6d28d9); }
.avatar-theme-y { background: linear-gradient(135deg, #0284c7, #0369a1); }
.avatar-theme-z { background: linear-gradient(135deg, #059669, #047857); }

.avatar-alert-badge {
  position: absolute;
  top: -4px;
  right: -4px;
  background-color: white;
  border-radius: 50%;
  width: 14px;
  height: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 1px 3px rgba(0,0,0,0.2);
}

.contact-name {
  font-weight: 600;
  color: #0f172a;
}

.session-id-pill {
  font-family: monospace;
  font-size: 11px;
  font-weight: 700;
  background-color: #f1f5f9;
  color: #475569;
  padding: 2px 6px;
  border-radius: 4px;
  border: 1px solid #e2e8f0;
}

.empty-value {
  color: #94a3b8;
}

.email-link {
  color: #0284c7;
  text-decoration: none;
  font-weight: 500;
}

.email-link:hover {
  text-decoration: underline;
}

.col-actions {
  width: 50px;
  text-align: center;
}

.action-menu-container {
  position: relative;
  display: inline-block;
}

.row-action-trigger {
  background: none;
  border: none;
  color: #94a3b8;
  padding: 6px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.row-action-trigger:hover {
  background-color: #e2e8f0;
  color: #0f172a;
}

.menu-backdrop {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 100;
}

.row-action-menu {
  position: absolute;
  right: 100%;
  top: 0;
  background-color: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -4px rgba(0, 0, 0, 0.05);
  padding: 4px;
  z-index: 101;
  min-width: 140px;
  margin-right: 4px;
}

.menu-item-btn {
  width: 100%;
  background: none;
  border: none;
  padding: 8px 10px;
  font-size: 12px;
  font-weight: 550;
  color: #475569;
  border-radius: 6px;
  display: flex;
  align-items: center;
  gap: 8px;
  text-align: left;
}

.menu-item-btn:hover {
  background-color: #f4f6fa;
  color: #0f172a;
}

.menu-item-btn.danger {
  color: #ef4444;
}

.menu-item-btn.danger:hover {
  background-color: #fee2e2;
  color: #ef4444;
}

.menu-separator {
  margin: 4px 0;
  border: 0;
  border-top: 1px solid #e2e8f0;
}

.table-empty-row {
  text-align: center;
  padding: 40px !important;
  color: #475569;
  font-weight: 500;
}

/* Escalations Panel Styling */
.escalations-panel {
  background-color: transparent;
  border: none;
  border-radius: 0;
  box-shadow: none;
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.panel-header {
  padding: 16px;
  border-bottom: 1px solid #e2e8f0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  background-color: #ffffff;
  flex-shrink: 0;
}

.panel-title-wrapper {
  display: flex;
  align-items: center;
  gap: 8px;
}

.panel-title {
  font-size: 15px;
  font-weight: 700;
  color: #0f172a;
}

.escalation-alert-icon {
  color: #ef4444;
}

@keyframes pulse {
  0%, 100% { opacity: 1; transform: scale(1); }
  50% { opacity: 0.7; transform: scale(1.1); }
}

.animate-pulse {
  animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
}

/* ── Shimmer Skeleton Loading ── */
@keyframes shimmer {
  0%   { background-position: -600px 0; }
  100% { background-position:  600px 0; }
}

.skeleton-cell {
  height: 14px;
  border-radius: 6px;
  background: linear-gradient(
    90deg,
    #f1f5f9 25%,
    #e2e8f0 50%,
    #f1f5f9 75%
  );
  background-size: 600px 100%;
  animation: shimmer 1.4s ease-in-out infinite;
}

.skeleton-avatar {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: linear-gradient(
    90deg,
    #f1f5f9 25%,
    #e2e8f0 50%,
    #f1f5f9 75%
  );
  background-size: 600px 100%;
  animation: shimmer 1.4s ease-in-out infinite;
  flex-shrink: 0;
}

.skeleton-row td {
  padding: 14px 16px;
  border-bottom: 1px solid #f1f5f9;
}

.escalation-count-badge {
  font-size: 10px;
  font-weight: 700;
  background-color: #fee2e2;
  color: #ef4444;
  padding: 3px 8px;
  border-radius: 12px;
}

.escalation-search-wrapper {
  padding: 10px 14px;
  border-bottom: 1px solid #e2e8f0;
  position: relative;
  background-color: #f8fafc;
}

.esc-search-icon {
  position: absolute;
  left: 22px;
  top: 18px;
  color: #94a3b8;
}

.esc-search-input {
  width: 100%;
  padding: 6px 12px 6px 28px;
  font-size: 12px;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  background-color: #ffffff;
}

.esc-search-input:focus {
  outline: none;
  border-color: #0284c7;
  box-shadow: 0 0 0 2px rgba(2, 132, 199, 0.1);
}

.escalations-list {
  flex: 1;
  overflow-y: auto;
  padding: 14px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  background-color: #fafbfc;
}

.escalations-empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  text-align: center;
  color: #94a3b8;
}

.empty-check-icon {
  color: #10b981;
  background-color: #d1fae5;
  padding: 6px;
  border-radius: 50%;
  margin-bottom: 12px;
}

.empty-text {
  font-size: 13px;
  font-weight: 555;
  color: #475569;
}

.escalation-card {
  background-color: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 12px 14px;
  display: flex;
  flex-direction: column;
  gap: 10px;
  transition: all 0.15s ease;
  box-shadow: 0 1px 2px rgba(0,0,0,0.02);
}

.escalation-card:hover {
  border-color: #cbd5e1;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05), 0 2px 4px -2px rgba(0, 0, 0, 0.05);
  transform: translateY(-1px);
}

.escalation-card.claimed {
  background-color: #d1fae5;
  border-color: #a7f3d0;
}

.card-top {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.severity-badge {
  font-size: 9px;
  font-weight: 700;
  text-transform: uppercase;
  padding: 2px 6px;
  border-radius: 4px;
}

.severity-critical {
  background-color: #fee2e2;
  color: #ef4444;
}

.severity-high {
  background-color: #ffedd5;
  color: #ea580c;
}

.severity-medium {
  background-color: #fef3c7;
  color: #d97706;
}

.esc-time {
  font-size: 11px;
  color: #94a3b8;
  font-weight: 500;
}

.card-body {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.esc-user-info {
  display: flex;
  align-items: center;
  gap: 10px;
}

.esc-avatar {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  object-fit: cover;
}

.esc-avatar-letter {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 11px;
  color: white;
}

.esc-text-details {
  display: flex;
  flex-direction: column;
}

.esc-username {
  font-size: 13px;
  font-weight: 600;
  color: #0f172a;
}

.esc-session-id {
  font-size: 10px;
  color: #94a3b8;
  font-family: monospace;
}

.esc-reason-box {
  background-color: #f8fafc;
  padding: 8px 10px;
  border-radius: 6px;
  border: 1px solid #e2e8f0;
}

.escalation-card.claimed .esc-reason-box {
  background-color: rgba(255,255,255,0.5);
  border-color: rgba(167, 243, 208, 0.5);
}

.reason-label {
  font-size: 10px;
  font-weight: 700;
  color: #475569;
  display: block;
  margin-bottom: 2px;
}

.reason-text {
  font-size: 12px;
  line-height: 1.4;
  color: #475569;
}

.card-actions {
  display: flex;
  gap: 6px;
  margin-top: 2px;
}

.esc-btn-primary {
  flex: 1;
  background-color: #0f172a;
  color: white;
  border: none;
  border-radius: 6px;
  padding: 6px 10px;
  font-size: 11px;
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
}

.esc-btn-primary:hover {
  background-color: #1e293b;
}

.esc-btn-secondary {
  background-color: #ffffff;
  border: 1px solid #e2e8f0;
  color: #475569;
  border-radius: 6px;
  padding: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.esc-btn-secondary:hover {
  border-color: #cbd5e1;
  color: #0f172a;
}

.claimed-status-bar {
  display: flex;
  align-items: center;
  gap: 6px;
  color: #10b981;
  font-size: 11px;
  font-weight: 600;
  padding: 4px 0;
}

/* Edit Contact Modal Styles */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(15, 23, 42, 0.6);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  animation: modalFadeIn 0.2s ease-out;
}

.modal-content {
  background: white;
  border-radius: 16px;
  width: 480px;
  max-width: 90%;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
  overflow: hidden;
  animation: modalSlideUp 0.3s cubic-bezier(0.16, 1, 0.3, 1);
}

.modal-header {
  padding: 20px 24px;
  border-bottom: 1px solid #e2e8f0;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.modal-title {
  font-size: 18px;
  font-weight: 700;
  color: #0f172a;
}

.modal-close-btn {
  background: none;
  border: none;
  color: #94a3b8;
  cursor: pointer;
  padding: 4px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.modal-close-btn:hover {
  background-color: #f1f5f9;
  color: #475569;
}

.modal-body {
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.form-label {
  font-size: 12px;
  font-weight: 700;
  color: #475569;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.form-input {
  padding: 10px 14px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  font-size: 14px;
  color: #0f172a;
  background-color: #f8fafc;
  transition: all 0.15s ease;
}

.form-input:focus {
  outline: none;
  border-color: #0284c7;
  background-color: #ffffff;
  box-shadow: 0 0 0 3px rgba(2, 132, 199, 0.1);
}

.modal-footer {
  padding: 16px 24px;
  border-top: 1px solid #e2e8f0;
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  background-color: #f8fafc;
}

.btn-cancel {
  background: none;
  border: 1px solid #e2e8f0;
  color: #475569;
  padding: 10px 16px;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
}

.btn-cancel:hover {
  background-color: #f1f5f9;
  border-color: #cbd5e1;
  color: #0f172a;
}

.btn-save {
  background-color: #0f172a;
  color: white;
  border: none;
  padding: 10px 16px;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
}

.btn-save:hover {
  background-color: #1e293b;
}

@keyframes modalFadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

@keyframes modalSlideUp {
  from { transform: translateY(20px); }
  to { transform: translateY(0); }
}

.dragging-active, 
.dragging-active * {
  cursor: col-resize !important;
  user-select: none !important;
  -webkit-user-select: none !important;
}
      `}</style>
      <div className="easy-app-container" style={{ height: "calc(100vh - 64px)", display: "flex", flexDirection: "column" }}>
        <div className="easy-body-wrapper">
          <div className="easy-body-area connected-panels-card">

            {/* Left Column: Contacts Table */}
            <section className="body-left-part">
              <div className="contacts-panel-wrapper card-container">
                {/* Table Toolbar */}
                <div className="table-toolbar">
                  <div className="toolbar-left">
                    <label className="select-all-label" style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                      <input
                        type="checkbox"
                        className="custom-checkbox"
                        checked={contacts.length > 0 && selectedIds.size === contacts.length}
                        onChange={handleSelectAll}
                      />
                      <span>Select all</span>
                    </label>

                    {selectedIds.size > 0 && (
                      <button
                        className="btn-primary"
                        style={{ display: "flex", alignItems: "center", gap: "6px", backgroundColor: "#ef4444", color: "#ffffff", padding: "6px 12px", border: "none", borderRadius: "6px", fontSize: "12.5px", fontWeight: "600", cursor: "pointer", marginLeft: "12px" }}
                        onClick={handleBulkDelete}
                      >
                        <Trash2 size={14} />
                        <span>Delete Selected ({selectedIds.size})</span>
                      </button>
                    )}

                    <div className="search-input-wrapper">
                      <Search size={16} className="search-icon" />
                      <input
                        type="text"
                        placeholder="Search all contacts"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="search-field"
                      />
                    </div>

                    {/* Channel Filter Dropdown */}
                    <div ref={channelDropdownRef} className="channel-dropdown-container" style={{ position: "relative", marginLeft: "12px", width: "160px", zIndex: 10 }}>
                      <div
                        onClick={() => setIsChannelDropdownOpen(!isChannelDropdownOpen)}
                        style={{
                          padding: "8px 12px",
                          border: "1px solid #cbd5e1",
                          borderRadius: "8px",
                          fontSize: "12.5px",
                          fontWeight: "500",
                          backgroundColor: "#f8fafc",
                          color: "#1e293b",
                          cursor: "pointer",
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          height: "36px",
                          boxSizing: "border-box"
                        }}
                      >
                        <span style={{ fontSize: "11.5px", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", maxWidth: "120px" }}>
                          {selectedChannels.length === 0 
                            ? "All Channels" 
                            : selectedChannels.length === 1 
                              ? channelOptions.find(opt => opt.value === selectedChannels[0])?.label 
                              : `${selectedChannels.length} Selected`}
                        </span>
                        <ChevronDown
                          size={14}
                          style={{
                            transition: "transform 0.2s ease",
                            transform: isChannelDropdownOpen ? "rotate(180deg)" : "rotate(0deg)",
                            color: "#64748b"
                          }}
                        />
                      </div>

                      {isChannelDropdownOpen && (
                        <div
                          style={{
                            position: "absolute",
                            top: "calc(100% + 4px)",
                            left: 0,
                            right: 0,
                            backgroundColor: "#ffffff",
                            border: "1px solid #e2e8f0",
                            borderRadius: "8px",
                            boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
                            zIndex: 100,
                            maxHeight: "240px",
                            overflowY: "auto"
                          }}
                        >
                          {channelOptions.map((opt) => {
                            const isSelected = opt.value === "" 
                              ? selectedChannels.length === 0 
                              : selectedChannels.includes(opt.value);
                            return (
                              <div
                                key={opt.value}
                                onClick={() => {
                                  if (opt.value === "") {
                                    setSelectedChannels([]);
                                  } else {
                                    const next = [...selectedChannels];
                                    const idx = next.indexOf(opt.value);
                                    if (idx > -1) {
                                      next.splice(idx, 1);
                                    } else {
                                      next.push(opt.value);
                                    }
                                    setSelectedChannels(next);
                                  }
                                  setCurrentPage(1);
                                }}
                                style={{
                                  padding: "8px 12px",
                                  fontSize: "12px",
                                  color: isSelected ? "#2563eb" : "#334155",
                                  backgroundColor: isSelected ? "#eff6ff" : "transparent",
                                  fontWeight: isSelected ? "600" : "400",
                                  cursor: "pointer",
                                  transition: "background-color 0.1s ease",
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "8px"
                                }}
                                onMouseEnter={(e) => {
                                  if (!isSelected) {
                                    e.currentTarget.style.backgroundColor = "#f1f5f9";
                                  }
                                }}
                                onMouseLeave={(e) => {
                                  if (!isSelected) {
                                    e.currentTarget.style.backgroundColor = "transparent";
                                  }
                                }}
                              >
                                <input
                                  type="checkbox"
                                  checked={isSelected}
                                  readOnly
                                  style={{ pointerEvents: "none", cursor: "pointer" }}
                                />
                                <span>{opt.label}</span>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="toolbar-right">
                    <div className="pagination-wrapper">
                      <span className="pagination-text">
                        {startItem} - {endItem} of {totalContacts}
                      </span>
                      <div className="pagination-actions">
                        <button
                          className="pagination-arrow-btn"
                          disabled={currentPage === 1 || loading}
                          onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                          aria-label="Previous page"
                        >
                          <ChevronLeft size={16} />
                        </button>
                        <button
                          className="pagination-arrow-btn"
                          disabled={currentPage >= Math.ceil(totalContacts / itemsPerPage) || loading}
                          onClick={() => setCurrentPage(p => p + 1)}
                          aria-label="Next page"
                        >
                          <ChevronRight size={16} />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Table Grid */}
                <div className="table-overflow-container">
                  {error ? (
                    <div className="p-8 text-center text-red-500 flex flex-col items-center justify-center gap-2">
                      <AlertCircle size={28} />
                      <p className="font-semibold text-sm">{error}</p>
                    </div>
                  ) : (
                    <table className="contacts-data-table">
                      <thead>
                        <tr>
                          <th className="col-checkbox"></th>
                          <th className="col-contact">Contact</th>
                          <th className="col-username">Username</th>
                          <th className="col-channel">Channel</th>
                          <th className="col-email">Email</th>
                          <th className="col-phone">Phone number</th>
                          <th className="col-location">Location</th>
                          <th className="col-actions"></th>
                        </tr>
                      </thead>
                      <tbody>
                        {loading ? (
                          // ── Shimmer skeleton rows ──
                          Array.from({ length: 8 }).map((_, i) => (
                            <tr key={`skel-${i}`} className="skeleton-row" style={{ opacity: 1 - i * 0.07 }}>
                              {/* Checkbox */}
                              <td className="col-checkbox">
                                <div className="skeleton-cell" style={{ width: "16px", height: "16px", borderRadius: "4px", margin: "0 auto" }} />
                              </td>
                              {/* Contact (avatar + name) */}
                              <td className="col-contact">
                                <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                                  <div className="skeleton-avatar" />
                                  <div className="skeleton-cell" style={{ width: `${70 + (i % 3) * 25}px` }} />
                                </div>
                              </td>
                              {/* Username */}
                              <td className="col-username">
                                <div className="skeleton-cell" style={{ width: `${50 + (i % 4) * 15}px` }} />
                              </td>
                              {/* Channel badge */}
                              <td className="col-channel">
                                <div className="skeleton-cell" style={{ width: "64px", height: "20px", borderRadius: "20px" }} />
                              </td>
                              {/* Email */}
                              <td className="col-email">
                                <div className="skeleton-cell" style={{ width: `${80 + (i % 3) * 20}px` }} />
                              </td>
                              {/* Phone */}
                              <td className="col-phone">
                                <div className="skeleton-cell" style={{ width: "90px" }} />
                              </td>
                              {/* Location */}
                              <td className="col-location">
                                <div className="skeleton-cell" style={{ width: `${40 + (i % 4) * 18}px` }} />
                              </td>
                              {/* Actions */}
                              <td className="col-actions" />
                            </tr>
                          ))
                        ) : contacts.length === 0 ? (
                          <tr>
                            <td colSpan="8" className="table-empty-row">
                              No contacts match your search query.
                            </td>
                          </tr>
                        ) : (
                          contacts.map((contact) => {
                            const isSelected = selectedIds.has(contact.id);

                            let locationVal = "--";
                            if (contact.custom_parameters) {
                              const params = typeof contact.custom_parameters === 'string' 
                                ? JSON.parse(contact.custom_parameters) 
                                : contact.custom_parameters;
                              if (params && params.location) {
                                locationVal = params.location;
                              }
                            }

                            return (
                              <tr
                                key={contact.id}
                                className={`contact-row ${isSelected ? "row-selected" : ""}`}
                                onClick={(e) => handleRowClick(e, contact.id)}
                                style={{ cursor: "pointer" }}
                              >
                                <td className="col-checkbox">
                                  <input
                                    type="checkbox"
                                    className="custom-checkbox"
                                    checked={isSelected}
                                    onChange={() => handleSelectRow(contact.id)}
                                    onClick={(e) => e.stopPropagation()}
                                  />
                                </td>

                                <td className="col-contact">
                                  <div className="contact-cell">
                                    <div className="avatar-wrapper">
                                      {(() => {
                                        const params = typeof contact.custom_parameters === 'string'
                                          ? JSON.parse(contact.custom_parameters)
                                          : contact.custom_parameters;
                                        
                                        let imgUrl = "";
                                        if (params?.avatar_url) {
                                          const apiBase = process.env.REACT_APP_API_BASE_URL || '';
                                          const baseDomain = apiBase.endsWith('/api') ? apiBase.slice(0, -4) : apiBase;
                                          imgUrl = `${baseDomain}${params.avatar_url}`;
                                        } else {
                                          const cSeed = params?.avatar_seed || contact.name || contact.userid || String(contact.id);
                                          imgUrl = `https://api.dicebear.com/7.x/micah/svg?seed=${cSeed}&backgroundColor=b6e3f4,c0aede,d1d4f9`;
                                        }
                                        
                                        return (
                                          <img
                                            src={imgUrl}
                                            alt="Avatar"
                                            className="contact-avatar-img animate-in fade-in duration-200"
                                          />
                                        );
                                      })()}

                                      {contact.is_escalated && (
                                        <div className="avatar-alert-badge" title="Escalated">
                                          <AlertCircle size={10} fill="var(--status-escalated)" stroke="#ffffff" strokeWidth={3} />
                                        </div>
                                      )}
                                    </div>
                                    <span className="contact-name">{contact.name}</span>
                                  </div>
                                </td>

                                <td className="col-username">
                                  <span className="contact-username" style={{ fontFamily: "monospace", fontSize: "12px", color: "#475569" }}>
                                    {(() => {
                                      const uid = contact.userid;
                                      if (!uid || uid === 'anonymous' || uid.toLowerCase() === 'value') return 'anonymous';
                                      const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
                                      if (uuidRegex.test(uid)) return 'anonymous';
                                      if (/^(widget|telegram|slack|freshchat|freshdesk|zendesk|email|whatsapp|webchat)_/i.test(uid)) return 'anonymous';
                                      return uid;
                                    })()}
                                  </span>
                                </td>

                                <td className="col-channel">
                                  {(() => {
                                    const chan = String(contact.custom_parameters?.channel || 'widget').toLowerCase();
                                    let label = 'Widget Chat';
                                    let bg = 'bg-blue-50 text-blue-600 border border-blue-100/50';
                                    
                                    if (chan === 'webchat') {
                                      label = 'Webchat';
                                      bg = 'bg-indigo-50 text-indigo-600 border border-indigo-100/50';
                                    } else if (chan === 'whatsapp') {
                                      label = 'WhatsApp';
                                      bg = 'bg-emerald-50 text-emerald-600 border border-emerald-100/50';
                                    } else if (chan === 'telegram') {
                                      label = 'Telegram';
                                      bg = 'bg-sky-50 text-sky-600 border border-sky-100/50';
                                    } else if (chan === 'slack') {
                                      label = 'Slack';
                                      bg = 'bg-purple-50 text-purple-600 border border-purple-100/50';
                                    } else if (chan === 'email') {
                                      label = 'Email';
                                      bg = 'bg-amber-50 text-amber-600 border border-amber-100/50';
                                    } else if (chan === 'freshdesk') {
                                      label = 'Freshdesk';
                                      bg = 'bg-emerald-50 text-emerald-600 border border-emerald-100/50';
                                    } else if (chan === 'freshchat') {
                                      label = 'Freshchat';
                                      bg = 'bg-teal-50 text-teal-600 border border-teal-100/50';
                                    } else if (chan === 'webhook') {
                                      label = 'Webhook';
                                      bg = 'bg-rose-50 text-rose-600 border border-rose-100/50';
                                    } else if (chan === 'zendesk') {
                                      label = 'Zendesk';
                                      bg = 'bg-orange-50 text-orange-600 border border-orange-100/50';
                                    }
                                    
                                    return (
                                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold ${bg}`}>
                                        {label}
                                      </span>
                                    );
                                  })()}
                                </td>

                                <td className="col-email">
                                  {(() => {
                                    const em = contact.email;
                                    const invalid = !em || em === 'N/A' || em.trim().toLowerCase() === 'value';
                                    if (invalid) return <span className="empty-value">--</span>;
                                    return (
                                      <a
                                        href={`mailto:${em}`}
                                        className="email-link"
                                        onClick={(e) => e.stopPropagation()}
                                      >
                                        {em}
                                      </a>
                                    );
                                  })()}
                                </td>

                                <td className="col-phone">
                                  {(() => {
                                    const ph = contact.phone;
                                    const invalid = !ph || ph === 'N/A' || ph.trim().toLowerCase() === 'value';
                                    return invalid ? <span className="empty-value">--</span> : ph;
                                  })()}
                                </td>

                                <td className="col-location">
                                  {locationVal === "--" ? <span className="empty-value">--</span> : locationVal}
                                </td>

                                <td className="col-actions">
                                  <div className="action-menu-container">
                                    <button
                                      className="row-action-trigger"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setActiveMenuId(activeMenuId === contact.id ? null : contact.id);
                                      }}
                                      aria-label="Actions"
                                    >
                                      <MoreVertical size={16} />
                                    </button>

                                    {activeMenuId === contact.id && (
                                      <>
                                        <div className="menu-backdrop" onClick={(e) => { e.stopPropagation(); setActiveMenuId(null); }} />
                                        <div className="row-action-menu animate-fade-in" onClick={(e) => e.stopPropagation()}>
                                          <button
                                            className="menu-item-btn"
                                            onClick={() => {
                                              if (contact.sessions && contact.sessions.length > 0) {
                                                navigate(`/analytics?tab=conversations&session_id=${contact.sessions[0].session_id}`);
                                              } else {
                                                navigate(`/analytics?tab=conversations`);
                                              }
                                              setActiveMenuId(null);
                                            }}
                                          >
                                            <Eye size={14} />
                                            <span>View Chats</span>
                                          </button>
                                          <hr className="menu-separator" />
                                          <button
                                            className="menu-item-btn danger"
                                            onClick={() => {
                                              handleDeleteClick(contact.id);
                                              setActiveMenuId(null);
                                            }}
                                          >
                                            <Trash2 size={14} />
                                            <span>Delete Contact</span>
                                          </button>
                                        </div>
                                      </>
                                    )}
                                  </div>
                                </td>
                              </tr>
                            );
                          })
                        )}
                      </tbody>
                    </table>
                  )}
                </div>
              </div>
            </section>
          </div>
        </div>



        {/* Delete Contact Modal */}
        <DeleteConfirmModal
          isOpen={deleteModalConfig.isOpen}
          onClose={() => setDeleteModalConfig(prev => ({ ...prev, isOpen: false }))}
          onConfirm={deleteModalConfig.onConfirm}
          title={deleteModalConfig.title}
          message={deleteModalConfig.message}
          confirmText="Delete Permanently"
        />
      </div>
    </>
  );
}

